import { AnnotationsApiClient, AnnotationsApiClientFactory } from '@ftr/api-annotations'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { Loading, RemoteData } from '@ftr/foundation'
import { catchError, from, map, Observable, of, shareReplay, startWith, switchMap } from 'rxjs'

export abstract class AnnotationsService {
  constructor(
    private readonly apiClientFactory: AnnotationsApiClientFactory,
    private readonly regionCache: CourtSystemRegionCache,
  ) {}

  protected performApiRequest<T, U>({
    requestFn,
    responseMapFn,
    responseErrorMapFn = result => RemoteData.failure(result),
    courtSystemId,
  }: {
    courtSystemId: string
    requestFn: (client: AnnotationsApiClient) => Promise<T>
    responseMapFn: (result: T) => RemoteData<U>
    responseErrorMapFn?: (result: Error) => RemoteData<U, Error>
  }): Observable<Loading | RemoteData<U, Error>> {
    return this.getApiClient(courtSystemId).pipe(
      switchMap(client => {
        return from(requestFn(client)).pipe(
          map(result => responseMapFn(result)),
          catchError(e => {
            if (e?.name === 'ZodError') {
              const issues: { received?: string }[] | undefined = e?.issues
              return of(responseErrorMapFn(new Error(issues?.map(issue => issue?.received).join(', '))))
            }

            return of(responseErrorMapFn(new Error('An unknown error occurred')))
          }),
        )
      }),
      startWith(RemoteData.loading()),
      shareReplay(1),
    )
  }

  protected getApiClient(courtSystemId: string): Observable<AnnotationsApiClient> {
    return this.regionCache
      .getOrFetchCourtSystem(of(courtSystemId))
      .pipe(map(({ region }) => this.apiClientFactory.build(region)))
  }
}
